(() => {
  const clamp = (number, min, max) => Math.max(min, Math.min(max, number));
  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
  const normalize = (value = '') => String(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const normalizeList = (value) => (Array.isArray(value) ? value : [value]).map(normalize).filter(Boolean);

  const formatMoney = (cents) => {
    const value = Number(cents || 0);
    const format = window.KROWN?.settings?.shop_money_format || '{{ amount }}';
    const iso = window.KROWN?.settings?.iso_code || '';
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const type = match?.[1] || 'amount';
    const options = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_space_separator: [2, ' ', ','],
      amount_no_decimals_with_space_separator: [0, ' ', ',']
    }[type] || [2, ',', '.'];
    const [precision, thousands, decimal] = options;
    const parts = (value / 100).toFixed(precision).split('.');
    const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    const amount = whole + (parts[1] ? decimal + parts[1] : '') + iso;
    return format.replace(match?.[0] || '{{ amount }}', amount);
  };

  const discountedPrice = (price, percent) => {
    const discount = clamp(Number(percent || 0), 0, 100);
    return Math.round(Number(price || 0) * (1 - discount / 100));
  };

  const trackBfaEvent = (eventName, parameters = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      feature: 'backpack_tools',
      tool_source: 'backpack_quiz',
      ...parameters
    });
  };

  class BackpackFinder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.config = this.readConfig();
      this.products = this.config.products || [];
      this.allPanels = [...this.querySelectorAll('[data-quiz-panel]')];
      this.panelByKey = new Map(this.allPanels.map((panel) => [panel.dataset.stepKey, panel]));
      this.progress = this.querySelector('[data-quiz-progress]');
      this.stepLabel = this.querySelector('[data-quiz-step-label]');
      this.backButton = this.querySelector('[data-quiz-back]');
      this.restartButton = this.querySelector('[data-quiz-restart]');
      this.message = this.querySelector('[data-quiz-message]');
      this.loadingPanel = this.querySelector('[data-quiz-loading]');
      this.resultPanel = this.querySelector('[data-quiz-result]');
      this.resultContent = this.querySelector('[data-quiz-result-content]');

      this.answers = {
        use: null,
        carryStyle: null,
        capacity: null,
        features: [],
        preference: null
      };
      this.currentStepKey = 'use';
      this.selectedColours = {};
      this.selectedAccessories = new Set();
      this.results = [];
      this.quizStarted = false;
      this.quizSessionId = this.getQuizSessionId();

      this.bind();
      this.restore();
      this.renderStep(false);

      this.track('backpack_quiz_viewed', {
        product_count: this.products.length
      });

      if (!this.products.length) this.announce(this.config.labels.noProducts);
    }

    getQuizSessionId() {
      const key = `${this.dataset.storageKey || 'bfaBackpackFinder'}:analyticsSession`;

      try {
        const existing = sessionStorage.getItem(key);
        if (existing) return existing;

        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        sessionStorage.setItem(key, id);
        return id;
      } catch (error) {
        return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      }
    }

    track(eventName, parameters = {}) {
      if (window.Shopify?.designMode) return;

      trackBfaEvent(eventName, {
        quiz_id: 'backpack_finder',
        quiz_version: '2026_08_capacity_rework',
        quiz_session_id: this.quizSessionId,
        ...parameters
      });
    }

    answerLabel(button) {
      const spans = [...button.querySelectorAll('span')];
      const label = spans.find((span) =>
        !span.classList.contains('backpack-finder__check')
        && span.getAttribute('aria-hidden') !== 'true'
      );

      return (label?.textContent || button.textContent || '').trim();
    }

    resultPosition(card) {
      return card?.classList.contains('backpack-finder-result--primary')
        ? 'primary'
        : 'alternative';
    }

    readConfig() {
      try {
        return JSON.parse(this.querySelector('[data-backpack-finder-config]').textContent);
      } catch (error) {
        console.error('Unable to parse backpack finder configuration.', error);
        return { labels: {}, settings: {}, products: [] };
      }
    }

    bind() {
      this.addEventListener('click', (event) => {
        const answer = event.target.closest('[data-quiz-answer]');
        if (answer) return this.selectAnswer(answer);

        if (event.target.closest('[data-quiz-continue]')) return this.move(1);
        if (event.target.closest('[data-quiz-back]')) return this.move(-1);

        if (event.target.closest('[data-quiz-restart]')) {
          this.track('backpack_quiz_restarted', {
            abandoned_step: this.getFlow().indexOf(this.currentStepKey) + 1,
            had_results: Boolean(this.results?.length)
          });
          return this.reset();
        }

        const colour = event.target.closest('[data-quiz-colour]');
        if (colour) return this.selectColour(colour);

        const accessory = event.target.closest('[data-quiz-accessory]');
        if (accessory) return this.toggleAccessory(accessory);

        const addButton = event.target.closest('[data-quiz-add]');
        if (addButton) return this.addSelection(addButton);

        const detailsLink = event.target.closest('[data-result-link]');
        if (detailsLink) {
          const card = detailsLink.closest('[data-result-group]');
          this.track('backpack_quiz_result_clicked', {
            interaction_type: 'view_details',
            result_position: this.resultPosition(card),
            product_group: card?.dataset.resultGroup || '',
            product_handle: detailsLink.pathname.split('/').filter(Boolean).pop() || ''
          });
          return;
        }

        if (event.target.closest('[data-quiz-compare]')) {
          this.track('backpack_quiz_result_clicked', {
            interaction_type: 'compare_results',
            primary_result: this.results?.[0]?.product?.groupHandle || '',
            alternative_result: this.results?.[1]?.product?.groupHandle || ''
          });
          return this.openComparisonPage();
        }
      });

      this.addEventListener('change', (event) => {
        const selector = event.target.closest('[data-quiz-accessory-variant]');
        if (selector) this.selectAccessoryVariant(selector);
      });
    }

    shouldShowCarryStyle() {
      return ['training', 'work', 'mixed'].includes(this.answers.use);
    }

    getFlow() {
      const flow = ['use'];
      if (this.shouldShowCarryStyle()) flow.push('carryStyle');
      flow.push('capacity', 'features', 'preference');
      return flow;
    }

    selectAnswer(button) {
      const panel = button.closest('[data-quiz-panel]');
      const key = panel.dataset.question;
      const mode = panel.dataset.mode;
      const value = button.dataset.value;

      if (!this.quizStarted) {
        this.quizStarted = true;
        this.track('backpack_quiz_started', {
          entry_step: this.getFlow().indexOf(this.currentStepKey) + 1
        });
      }

      if (mode === 'multiple') {
        const values = [...(this.answers[key] || [])];
        const index = values.indexOf(value);

        if (index >= 0) values.splice(index, 1);
        else if (values.length < Number(this.dataset.maxFeatures || 3)) values.push(value);
        else {
          this.announce(`Choose up to ${this.dataset.maxFeatures || 3} features.`);
          return;
        }

        const selected = values.includes(value);
        this.answers[key] = values;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
        panel.querySelector('[data-feature-count]').textContent = values.length;
        panel.querySelector('[data-quiz-continue]').disabled = values.length === 0;

        this.track('backpack_quiz_answered', {
          question_id: key,
          question_step: this.getFlow().indexOf(this.currentStepKey) + 1,
          answer_value: value,
          answer_label: this.answerLabel(button),
          answer_action: selected ? 'selected' : 'deselected'
        });

        this.save();
        return;
      }

      this.answers[key] = value;
      if (key === 'use' && !this.shouldShowCarryStyle()) this.answers.carryStyle = null;

      panel.querySelectorAll('[data-quiz-answer]').forEach((candidate) => {
        const selected = candidate === button;
        candidate.classList.toggle('is-selected', selected);
        candidate.setAttribute('aria-checked', String(selected));
      });

      this.track('backpack_quiz_answered', {
        question_id: key,
        question_step: this.getFlow().indexOf(this.currentStepKey) + 1,
        answer_value: value,
        answer_label: this.answerLabel(button),
        answer_action: 'selected'
      });

      this.save();
      window.setTimeout(() => this.move(1), Number(this.dataset.autoAdvanceDelay || 300));
    }

    move(direction) {
      const flow = this.getFlow();
      const currentIndex = Math.max(0, flow.indexOf(this.currentStepKey));
      const nextIndex = currentIndex + direction;

      if (nextIndex >= flow.length) {
        this.showLoading();
        return;
      }
      if (nextIndex < 0) return;

      this.currentStepKey = flow[nextIndex];
      this.renderStep(true, direction < 0 ? 'back' : 'forward');
      this.save();
    }

    renderStep(animate = false, direction = 'forward') {
      this.hideOutcome();
      const flow = this.getFlow();
      if (!flow.includes(this.currentStepKey)) this.currentStepKey = flow[0];
      const currentIndex = flow.indexOf(this.currentStepKey);
      const currentPanel = this.panelByKey.get(this.currentStepKey);

      this.progress.style.width = `${((currentIndex + 1) / flow.length) * 100}%`;
      this.stepLabel.textContent = `${this.config.labels.step} ${currentIndex + 1} ${this.config.labels.of} ${flow.length}`;
      this.backButton.hidden = currentIndex === 0;
      this.restartButton.hidden = currentIndex === 0;

      this.allPanels.forEach((panel) => {
        const active = panel === currentPanel;
        panel.hidden = !active;
        panel.classList.remove('is-active', 'is-entering-forward', 'is-entering-back');
        if (!active) return;

        panel.classList.add('is-active');
        if (animate) {
          panel.classList.add(direction === 'back' ? 'is-entering-back' : 'is-entering-forward');
          window.setTimeout(() => panel.classList.remove('is-entering-forward', 'is-entering-back'), 280);
        }
      });

      if (animate) currentPanel?.querySelector('button')?.focus({ preventScroll: true });
    }

    showLoading() {
      const currentPanel = this.panelByKey.get(this.currentStepKey);
      if (currentPanel) {
        currentPanel.hidden = true;
        currentPanel.classList.remove('is-active');
      }
      this.loadingPanel.hidden = false;
      this.loadingPanel.classList.add('is-active');
      this.progress.style.width = '100%';
      this.backButton.hidden = true;
      window.setTimeout(() => this.showResult(), 650);
    }

    showResult() {
      const scored = this.scoreProducts();
      if (!scored.length) {
        this.loadingPanel.hidden = true;
        this.announce(this.config.labels.noProducts);
        return;
      }

      this.results = this.selectResults(scored);
      this.loadingPanel.hidden = true;
      this.loadingPanel.classList.remove('is-active');
      this.resultPanel.hidden = false;
      this.resultPanel.classList.add('is-active');
      this.renderResults();
      this.stepLabel.textContent = this.config.labels.bestMatch;
      this.restartButton.hidden = false;

      this.track('backpack_quiz_completed', {
        use_case: this.answers.use || '',
        carry_style: this.answers.carryStyle || '',
        capacity_choice: this.answers.capacity || '',
        preference: this.answers.preference || '',
        selected_features: (this.answers.features || []).join('|'),
        primary_result: this.results[0]?.product?.groupHandle || '',
        primary_tier: this.results[0]?.tier || '',
        alternative_result: this.results[1]?.product?.groupHandle || '',
        alternative_tier: this.results[1]?.tier || ''
      });

      this.dispatchEvent(new CustomEvent('bfa:quiz:completed', {
        bubbles: true,
        detail: {
          answers: this.answers,
          results: this.results.map((result) => ({
            groupHandle: result.product.groupHandle,
            score: result.score,
            tier: result.tier
          }))
        }
      }));
    }

    scoreProducts() {
      return this.products
        .filter((product) => this.isFormatEligible(product))
        .map((product) => this.scoreProduct(product))
        .filter(Boolean)
        .sort((a, b) => b.score - a.score);
    }

    isFormatEligible(product) {
      const style = this.answers.carryStyle;

      if (style === 'tote') return Boolean(product.isTote);
      if (style === 'backpack') return !product.isTote;

      // Travel and competition flows do not ask about Tote vs Backpack.
      if (!this.shouldShowCarryStyle()) return !product.isTote;

      return true;
    }

    selectResults(scored) {
      if (!scored.length) return [];

      const preference = normalize(this.answers.preference);
      const toteOnly = this.answers.carryStyle === 'tote';

      let primaryPool = scored;

      // Tote is a format choice first. The current Tote range is Value-positioned,
      // so do not make a Premium/Balanced answer eliminate all Tote recommendations.
      if (!toteOnly) {
        const tierMatches = scored.filter((result) => result.tier === preference);
        if (tierMatches.length) primaryPool = tierMatches;

        // Pro Series is only eligible as the top result for maximum comfort/premium.
        if (preference !== 'premium') {
          const withoutProSeries = primaryPool.filter((result) => !this.isProSeries(result.product));
          if (withoutProSeries.length) primaryPool = withoutProSeries;
        }
      }

      const primary = primaryPool[0] || scored[0];
      if (!primary) return [];

      const remaining = scored.filter((result) =>
        result.product.groupHandle !== primary.product.groupHandle
      );

      if (!remaining.length) return [primary];

      let alternative = null;

      if (!toteOnly) {
        const lowerTierOrder = {
          premium: ['balanced', 'value'],
          balanced: ['value'],
          value: []
        }[primary.tier] || [];

        for (const tier of lowerTierOrder) {
          alternative = remaining.find((result) => result.tier === tier);
          if (alternative) break;
        }

        // If there is no lower range, offer the best genuinely different range.
        if (!alternative) {
          alternative = remaining.find((result) => result.tier !== primary.tier);
        }
      }

      // Totes currently share the same commercial tier, or a different tier may
      // simply not exist. In that case, use the next strongest distinct product.
      if (!alternative) alternative = remaining[0];

      return [primary, alternative].filter(Boolean);
    }

    isProSeries(product) {
      return /\bpro\s*series\b/i.test(
        `${product?.groupTitle || ''} ${product?.shortLabel || ''}`
      );
    }

    scoreProduct(product) {
      const colour = this.getColour(product);
      if (!colour) return null;

      let score = colour.available ? 2 : -30;
      const generalReasons = [];
      const matchedFeatureReasons = [];
      const bestFor = normalizeList(product.bestFor);
      const tier = this.getPriceTier(product);

      const useMap = {
        training: ['training', 'pilates', 'meal prep'],
        work: ['work', 'everyday'],
        travel: ['travel', 'carry on', 'long haul'],
        competition: ['compete'],
        mixed: ['everyday', 'training', 'work', 'travel', 'pilates']
      };

      const useSignals = useMap[this.answers.use] || [];
      const useHits = useSignals.filter((signal) => bestFor.includes(signal)).length;

      // Use case fine-tunes within the commercial/capacity range rather than
      // deciding the range itself.
      if (useHits) {
        score += 4 + Math.max(0, useHits - 1);
        generalReasons.push(this.useReason(this.answers.use));
      } else {
        score -= 1;
      }

      const capacityScore = this.scoreCapacity(product);
      score += capacityScore.points;
      if (capacityScore.reason) generalReasons.push(capacityScore.reason);

      (this.answers.features || []).forEach((feature) => {
        const match = this.featureMatch(product, feature);
        score += match.score;

        if (match.matches && match.reason) {
          matchedFeatureReasons.push(match.reason);
        }
      });

      // What matters most is deliberately dominant. The top recommendation is
      // subsequently restricted to the selected tier in selectResults().
      const preferenceScore = this.scorePreference(product);
      score += preferenceScore.points;
      if (preferenceScore.reason) generalReasons.push(preferenceScore.reason);

      // Always surface matched customer-selected features before generic reasons,
      // so the result feels directly tied to their answers.
      const reasons = [
        ...matchedFeatureReasons,
        ...generalReasons
      ];

      return {
        product,
        colour,
        tier,
        score,
        matchedFeatureReasons: [...new Set(matchedFeatureReasons)],
        reasons: [...new Set(reasons)].slice(0, 4)
      };
    }

    featureMatch(product, feature) {
      switch (feature) {
        case 'shoes': {
          const matches = Boolean(product.features?.shoes);
          return {
            matches,
            score: matches ? 7 : -7,
            reason: matches ? 'Has the dedicated shoe compartment you selected' : ''
          };
        }

        case 'wetBag': {
          const matches = Boolean(product.features?.wetBag);
          return {
            matches,
            score: matches ? 6 : -6,
            reason: matches ? 'Includes the separate wet-kit storage you selected' : ''
          };
        }

        case 'waterResistant': {
          const matches = Boolean(product.features?.waterResistant);
          return {
            matches,
            score: matches ? 6 : -5,
            reason: matches ? 'Provides the water resistance you selected' : ''
          };
        }

        case 'patchSpace': {
          const hasPatchSpace = Boolean(product.features?.patchSpace);
          const personalisationSpace = normalize(
            product.features?.personalisationSpace || 'none'
          );

          if (!hasPatchSpace) {
            return {
              matches: false,
              score: -8,
              reason: ''
            };
          }

          if (personalisationSpace === 'extended') {
            return {
              matches: true,
              score: 14,
              reason: 'Offers extended space for patches and personalisation'
            };
          }

          if (personalisationSpace === 'standard') {
            return {
              matches: true,
              score: 7,
              reason: 'Includes the personalisation and patch space you selected'
            };
          }

          return {
            matches: true,
            score: 4,
            reason: 'Includes hook-and-loop space for patches'
          };
        }

        case 'dualBottleStorage': {
          const matches = normalize(product.features?.bottleHolders) === 'dual';
          return {
            matches,
            score: matches ? 6 : -5,
            reason: matches ? 'Includes the dual bottle storage you selected' : ''
          };
        }

        case 'cabinFriendly': {
          const matches = Boolean(product.features?.cabinFriendly);
          return {
            matches,
            score: matches ? 7 : -7,
            reason: matches ? 'Designed with cabin travel in mind' : ''
          };
        }

        default:
          return {
            matches: false,
            score: 0,
            reason: ''
          };
      }
    }

    scoreCapacity(product) {
      const litres = this.capacityNumber(product.capacity);
      if (!litres) return { points: 0, reason: '' };

      const answer = this.answers.capacity;

      // Tote sizes are a separate 14L / 20L compact-plus system.
      let target;
      if (product.isTote) {
        target = answer === 'compact' ? 14 : 20;
      } else {
        target = {
          compact: 25,
          all_rounder: 35,
          maximum: 45,
          travel: 45
        }[answer] || 35;
      }

      const difference = Math.abs(litres - target);
      let points;

      if (difference <= 2) points = 12;
      else if (difference <= 5) points = 10;
      else if (difference <= 10) points = 5;
      else if (difference <= 15) points = 1;
      else points = -5;

      const reason = points >= 5
        ? `${product.capacity || `${litres}L`} capacity closely matches what you carry`
        : '';

      return { points, reason };
    }

    scorePreference(product) {
      const preference = normalize(this.answers.preference);
      const tier = this.getPriceTier(product);

      if (preference === tier) {
        return {
          points: 20,
          reason: {
            value: 'Matches your preference for the best value',
            balanced: 'Matches your preferred balance of price and performance',
            premium: 'Matches your preference for maximum comfort and organisation'
          }[preference] || ''
        };
      }

      const adjacent = (
        (preference === 'premium' && tier === 'balanced')
        || (preference === 'balanced' && ['value', 'premium'].includes(tier))
        || (preference === 'value' && tier === 'balanced')
      );

      return {
        points: adjacent ? 4 : 0,
        reason: ''
      };
    }

    getPriceTier(product) {
      const tier = normalize(product.pricePositioning);
      return ['value', 'balanced', 'premium'].includes(tier)
        ? tier
        : 'balanced';
    }

    useReason(use) {
      return {
        training: 'Designed for training, gym or studio routines',
        work: 'Well suited to work and everyday commuting',
        travel: 'Designed around travel and longer journeys',
        competition: 'Well suited to competition and event days',
        mixed: 'Versatile enough for your mixed routine'
      }[use] || '';
    }

    capacityNumber(value) {
      const match = String(value || '').match(/(\d+(?:\.\d+)?)\s*l/i);
      return match ? Number(match[1]) : 0;
    }

    getColour(product) {
      const savedHandle = this.selectedColours[product.groupHandle];
      return product.colours?.find((colour) => colour.handle === savedHandle)
        || product.colours?.find((colour) => colour.available)
        || product.colours?.[0]
        || null;
    }

    renderResults() {
      const primary = this.results[0];
      const alternative = this.results[1];
      if (!primary) return;

      [primary, alternative].filter(Boolean).forEach((result) => {
        if (!this.selectedColours[result.product.groupHandle]) {
          this.selectedColours[result.product.groupHandle] = result.colour.handle;
        }
      });

      this.resultContent.innerHTML = `${this.resultCard(primary, true)}${alternative ? this.resultCard(alternative, false) : ''}`;
      this.syncAccessoryState();
    }

    cabinDisclaimer(product) {
      if (!product?.features?.cabinFriendly) return '';

      return `
        <div class="backpack-finder-result__cabin-note kt-text-size--xsmall"><sup>*</sup>Cabin baggage allowances vary by airline and fare type. Please check your airline’s current size and weight restrictions before travelling.</div>
      `;
    }

    resultBadge(result, primary) {
      if (primary) return this.config.labels.bestMatch;

      const tierRank = { value: 0, balanced: 1, premium: 2 };
      const primaryTier = this.results?.[0]?.tier;
      const isLowerRange = (
        primaryTier in tierRank
        && result?.tier in tierRank
        && tierRank[result.tier] < tierRank[primaryTier]
      );

      return isLowerRange
        ? (this.config.labels.lowerAlternative || 'A lower-priced alternative')
        : this.config.labels.alternative;
    }

    resultCard(result, primary) {
      const product = result.product;
      const colour = this.getColour(product);
      const match = clamp(70 + Math.round((result.score / 48) * 30), 70, 99);
      const addLabel = colour.available
        ? `${this.config.labels.addToCart} - ${formatMoney(colour.price)}`
        : this.config.labels.unavailable;

      return `
        <article
          class="backpack-finder-result ${primary ? 'backpack-finder-result--primary' : ''}"
          data-result-group="${escapeHtml(product.groupHandle)}"
          ${primary ? '' : 'data-alternative-card'}
        >
          <div class="backpack-finder-result__badge">
            ${escapeHtml(this.resultBadge(result, primary))}
            ${primary ? ` · ${match}% match` : ''}
          </div>

          <div class="backpack-finder-result__layout">
            <div class="backpack-finder-result__media">
              <img src="${escapeHtml(colour.image)}" alt="${escapeHtml(colour.title)}" loading="eager" data-result-image>
            </div>

            <div class="backpack-finder-result__content">
              <h2 class="kt-product-title kt-text-font--headings kt-text-size--h4 kt-text-transform--uppercase kt-text-line-height--smaller kt-text-weight--bold">
                ${escapeHtml(product.shortLabel || product.groupTitle)}
              </h2>

              <div class="backpack-finder-result__price">
                <strong data-result-price>${escapeHtml(colour.priceFormatted)}</strong>
                ${colour.compareAtPriceFormatted ? `<s data-result-compare-price>${escapeHtml(colour.compareAtPriceFormatted)}</s>` : ''}
              </div>

              <div class="backpack-finder-result__reasons kt-text-size--medium">
                <strong>${escapeHtml(this.config.labels.why)}</strong>
                <ul>${result.reasons.map((reason) => `<li>${escapeHtml(reason)}</li>`).join('')}</ul>
              </div>

              ${this.cabinDisclaimer(product)}

              ${this.coloursMarkup(product, colour)}
              ${primary ? this.accessoriesMarkup(product) : ''}

              <div class="backpack-finder-result__actions">
                <button
                  type="button"
                  class="kt-button kt-button--small kt-button--solid"
                  data-quiz-add
                  data-group="${escapeHtml(product.groupHandle)}"
                  data-base-label="${escapeHtml(this.config.labels.addToCart)}"
                  ${colour.available ? '' : 'disabled'}
                >${escapeHtml(addLabel)}</button>

                <a class="kt-button kt-button--small kt-button--outline" href="${escapeHtml(colour.url)}" data-result-link>
                  ${escapeHtml(this.config.labels.viewDetails)}
                </a>
              </div>

              ${primary && this.results[1] ? `<button type="button" class="backpack-finder-result__compare" data-quiz-compare>${escapeHtml(this.config.labels.compare)}</button>` : ''}
            </div>
          </div>

          <p class="backpack-finder-result__message" data-result-message role="status"></p>
        </article>
      `;
    }

    coloursMarkup(product, selectedColour) {
      if (!product.colours?.length) return '';

      return `
        <div class="backpack-finder-colours">
          <div class="kt-text-size--small kt-text-transform--uppercase">
            ${escapeHtml(this.config.labels.colour)}: <strong data-result-colour-name>${escapeHtml(selectedColour.title)}</strong>
          </div>
          <div class="backpack-finder-colours__list">
            ${product.colours.map((colour) => `
              <button
                type="button"
                class="backpack-finder-colour ${colour.handle === selectedColour.handle ? 'is-selected' : ''}"
                data-quiz-colour
                data-group="${escapeHtml(product.groupHandle)}"
                data-handle="${escapeHtml(colour.handle)}"
                aria-label="${escapeHtml(colour.title)}"
                aria-pressed="${colour.handle === selectedColour.handle}"
                ${colour.available ? '' : 'disabled'}
              >
                <img src="${escapeHtml(colour.image)}" alt="" loading="lazy">
              </button>
            `).join('')}
          </div>
        </div>
      `;
    }

    getAddonDiscountPercent(product) {
      const productName = String(
        product?.groupTitle ||
        product?.shortLabel ||
        ''
      ).toLowerCase();

      if (productName.includes('tote')) {
        return 25;
      }

      return Number(this.config.settings.addonDiscountPercent || 35);
    }

    accessoriesMarkup(product) {
      if (!this.config.settings.showAccessories || !product.accessories?.length) return '';

      const discountPercent = this.getAddonDiscountPercent(product);
      const accessories = product.accessories.slice(0, this.config.settings.maxAccessories);

      return `
        <div class="backpack-finder-accessories">
          <div class="backpack-finder-accessories__header">
            <p class="backpack-finder-accessories__heading">${escapeHtml(this.config.labels.completeSetup)}</p>
            <p class="backpack-finder-accessories__subheading">Select recommended accessories and patches with a ${discountPercent}% discount and add everything together.</p>
          </div>

          <div class="backpack-finder-accessories__list">
            ${accessories.map((accessory) => {
              const variants = Array.isArray(accessory.variants) && accessory.variants.length
                ? accessory.variants
                : [{
                    id: accessory.variantId,
                    title: 'Default Title',
                    available: accessory.available,
                    price: accessory.price,
                    priceFormatted: accessory.priceFormatted
                  }];
              const selectedVariant = variants.find((variant) => variant.available) || variants[0];
              const selectedPrice = Number(selectedVariant?.price || 0);
              const selectedDiscountedPrice = discountedPrice(selectedPrice, discountPercent);
              const hasOptions = variants.length > 1 || normalize(selectedVariant?.title) !== 'default title';

              const variantSelector = hasOptions ? `
                <select
                  class="backpack-finder-accessory__variant"
                  data-quiz-accessory-variant
                  data-previous-variant-id="${selectedVariant?.id || ''}"
                  aria-label="Choose ${escapeHtml(accessory.title)} option"
                >
                  ${variants.map((variant) => {
                    const variantPrice = Number(variant.price || 0);
                    const variantDiscountedPrice = discountedPrice(variantPrice, discountPercent);
                    return `
                      <option
                        value="${variant.id}"
                        data-price="${variantPrice}"
                        data-discounted-price="${variantDiscountedPrice}"
                        data-price-formatted="${escapeHtml(variant.priceFormatted || '')}"
                        data-discounted-price-formatted="${escapeHtml(formatMoney(variantDiscountedPrice))}"
                        ${variant.id === selectedVariant?.id ? 'selected' : ''}
                        ${variant.available ? '' : 'disabled'}
                      >${escapeHtml(variant.title)}${variant.available ? '' : ' — Sold out'}</option>
                    `;
                  }).join('')}
                </select>
              ` : '';

              return `
                <div
                  class="backpack-finder-accessory ${selectedVariant?.available ? '' : 'is-disabled'}"
                  data-accessory-product-id="${accessory.productId}"
                >
                  <label class="backpack-finder-accessory__main">
                    <span class="backpack-finder-accessory__selection">
                      <input
                        type="checkbox"
                        class="backpack-finder-accessory__input"
                        data-quiz-accessory
                        data-variant-id="${selectedVariant?.id || ''}"
                        data-price="${selectedPrice}"
                        data-discounted-price="${selectedDiscountedPrice}"
                        ${selectedVariant?.available ? '' : 'disabled'}
                      >
                      <span class="backpack-finder-accessory__check" aria-hidden="true">
                        <svg viewBox="0 0 12 10" fill="none"><path d="m1 5 3 3 7-7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                      </span>
                    </span>

                    <span class="backpack-finder-accessory__media">
                      ${accessory.image ? `<img src="${escapeHtml(accessory.image)}" alt="" loading="lazy" width="64" height="64">` : ''}
                    </span>

                    <span class="backpack-finder-accessory__content">
                      <span class="backpack-finder-accessory__title">${escapeHtml(accessory.title)}</span>
                      ${variantSelector}
                    </span>

                    <span class="backpack-finder-accessory__price" data-accessory-price>
                      <strong data-accessory-discounted-price>${escapeHtml(formatMoney(selectedDiscountedPrice))}</strong>
                      <s data-accessory-original-price>${escapeHtml(selectedVariant?.priceFormatted || formatMoney(selectedPrice))}</s>
                    </span>
                  </label>
                </div>
              `;
            }).join('')}
          </div>
        </div>
      `;
    }

    selectAccessoryVariant(selector) {
      const row = selector.closest('.backpack-finder-accessory');
      const input = row?.querySelector('[data-quiz-accessory]');
      const option = selector.selectedOptions?.[0];
      if (!row || !input || !option) return;

      const previousVariantId = String(selector.dataset.previousVariantId || input.dataset.variantId || '');
      const nextVariantId = String(option.value || '');
      const wasChecked = input.checked;

      if (wasChecked && previousVariantId) this.selectedAccessories.delete(previousVariantId);

      input.dataset.variantId = nextVariantId;
      input.dataset.price = String(option.dataset.price || 0);
      input.dataset.discountedPrice = String(option.dataset.discountedPrice || option.dataset.price || 0);
      input.disabled = option.disabled;
      row.classList.toggle('is-disabled', option.disabled);

      const discountedElement = row.querySelector('[data-accessory-discounted-price]');
      const originalElement = row.querySelector('[data-accessory-original-price]');
      if (discountedElement) discountedElement.textContent = option.dataset.discountedPriceFormatted || '';
      if (originalElement) originalElement.textContent = option.dataset.priceFormatted || '';

      if (option.disabled && wasChecked) {
        input.checked = false;
        row.classList.remove('is-selected');
      } else if (wasChecked && nextVariantId) {
        this.selectedAccessories.add(nextVariantId);
      }

      selector.dataset.previousVariantId = nextVariantId;

      const card = row.closest('[data-result-group]');
      this.track('backpack_quiz_result_clicked', {
        interaction_type: 'addon_variant_changed',
        result_position: this.resultPosition(card),
        product_group: card?.dataset.resultGroup || '',
        addon_product_id: row.dataset.accessoryProductId || '',
        addon_variant_id: nextVariantId
      });

      this.updateCardTotal(card);
    }

    selectColour(button) {
      const groupHandle = button.dataset.group;
      const productHandle = button.dataset.handle;
      const product = this.products.find((item) => item.groupHandle === groupHandle);
      const colour = product?.colours.find((item) => item.handle === productHandle);
      if (!product || !colour) return;

      this.selectedColours[groupHandle] = productHandle;
      const card = button.closest('[data-result-group]');

      card.querySelectorAll('[data-quiz-colour]').forEach((candidate) => {
        const selected = candidate === button;
        candidate.classList.toggle('is-selected', selected);
        candidate.setAttribute('aria-pressed', String(selected));
      });

      const image = card.querySelector('[data-result-image]');
      if (image) {
        image.src = colour.image;
        image.alt = colour.title || '';
      }

      const price = card.querySelector('[data-result-price]');
      if (price) price.textContent = colour.priceFormatted;

      let comparePrice = card.querySelector('[data-result-compare-price]');
      if (colour.compareAtPriceFormatted) {
        if (!comparePrice && price) {
          comparePrice = document.createElement('s');
          comparePrice.dataset.resultComparePrice = '';
          price.insertAdjacentElement('afterend', comparePrice);
        }
        comparePrice.textContent = colour.compareAtPriceFormatted;
      } else if (comparePrice) {
        comparePrice.remove();
      }

      const link = card.querySelector('[data-result-link]');
      if (link) link.href = colour.url;

      const colourName = card.querySelector('[data-result-colour-name]');
      if (colourName) colourName.textContent = colour.title || '';

      const addButton = card.querySelector('[data-quiz-add]');
      if (addButton) addButton.disabled = !colour.available;

      this.track('backpack_quiz_result_clicked', {
        interaction_type: 'colour_selected',
        result_position: this.resultPosition(card),
        product_group: groupHandle,
        product_handle: productHandle,
        colour_name: colour.title || ''
      });

      this.updateCardTotal(card);
      this.save();
    }

    toggleAccessory(input) {
      const variantId = String(input.dataset.variantId || '');
      if (input.checked) this.selectedAccessories.add(variantId);
      else this.selectedAccessories.delete(variantId);

      const row = input.closest('.backpack-finder-accessory');
      const card = input.closest('[data-result-group]');
      row?.classList.toggle('is-selected', input.checked);

      this.track('backpack_quiz_result_clicked', {
        interaction_type: input.checked ? 'addon_selected' : 'addon_deselected',
        result_position: this.resultPosition(card),
        product_group: card?.dataset.resultGroup || '',
        addon_product_id: row?.dataset.accessoryProductId || '',
        addon_variant_id: variantId
      });

      this.updateCardTotal(card);
    }

    syncAccessoryState() {
      this.querySelectorAll('[data-quiz-accessory]').forEach((input) => {
        const selected = this.selectedAccessories.has(String(input.dataset.variantId));
        input.checked = selected;
        input.closest('.backpack-finder-accessory')?.classList.toggle('is-selected', selected);
      });
      this.querySelectorAll('[data-result-group]').forEach((card) => this.updateCardTotal(card));
    }

    updateCardTotal(card) {
      if (!card) return;
      const addButton = card.querySelector('[data-quiz-add]');
      if (!addButton) return;

      const product = this.products.find((item) => item.groupHandle === addButton.dataset.group);
      const colour = product ? this.getColour(product) : null;
      if (!colour?.available) {
        addButton.textContent = this.config.labels.unavailable;
        return;
      }

      let total = Number(colour.price || 0);
      card.querySelectorAll('[data-quiz-accessory]:checked').forEach((input) => {
        total += Number(input.dataset.discountedPrice || input.dataset.price || 0);
      });
      addButton.textContent = `${addButton.dataset.baseLabel || this.config.labels.addToCart} - ${formatMoney(total)}`;
    }

    async addSelection(button) {
      const product = this.products.find((item) => item.groupHandle === button.dataset.group);
      const colour = product ? this.getColour(product) : null;
      if (!product || !colour?.available) return;

      const card = button.closest('[data-result-group]');
      const items = [{
        id: Number(colour.variantId),
        quantity: 1,
        properties: { _source: 'backpack_finder', "_backpack_finder":"true", _quiz_group: product.groupHandle }
      }];

      card.querySelectorAll('[data-quiz-accessory]:checked').forEach((input) => {
        items.push({
          id: Number(input.dataset.variantId),
          quantity: 1,
          properties: { _source: 'backpack_finder', "_backpack_finder":"true", _linked_backpack_variant: colour.variantId }
        });
      });

      const original = button.textContent;
      button.disabled = true;
      button.textContent = this.config.labels.adding;

      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Unable to add these products.');

        button.textContent = this.config.labels.added;
        if (typeof window.refreshCart === 'function') await window.refreshCart();
        document.dispatchEvent(new CustomEvent('cart-updated', { detail: { source: 'backpack-finder' } }));
        document.dispatchEvent(new CustomEvent('krown:cart:update', { detail: { source: 'backpack-finder' } }));

        const selectedAddonIds = [...card.querySelectorAll('[data-quiz-accessory]:checked')]
          .map((input) => String(input.dataset.variantId || ''))
          .filter(Boolean);

        this.track('backpack_quiz_add_to_cart', {
          result_position: this.resultPosition(card),
          product_group: product.groupHandle,
          product_handle: colour.handle,
          variant_id: String(colour.variantId),
          addon_count: selectedAddonIds.length,
          selected_addons: selectedAddonIds.join('|'),
          addon_discount_percent: this.getAddonDiscountPercent(product)
        });

        window.setTimeout(() => {
          button.disabled = false;
          this.updateCardTotal(card);
        }, 1600);
      } catch (error) {
        button.disabled = false;
        button.textContent = original;
        card.querySelector('[data-result-message]').textContent = error.message;
      }
    }

    openComparisonPage() {
      if (!this.results?.length) return;
      const storageKey = this.dataset.comparisonKey || 'bfaBackpackComparison';
      const state = { version: 3, products: [] };

      this.results.slice(0, 2).forEach((result) => {
        const product = result.product;
        const colour = this.getColour(product);
        state.products.push({
          groupHandle: product.groupHandle,
          groupTitle: product.groupTitle,
          shortLabel: product.shortLabel,
          selectedProductHandle: colour.handle,
          specs: {
            bestFor: product.bestFor,
            capacity: product.capacity,
            dimensions: product.dimensions,
            emptyWeight: product.emptyWeight,
            warranty: product.warranty,
            hookLoopPatchSpace: !!product.features.patchSpace,
            laptopCompartment: !!product.features.laptop,
            maximumLaptopSize: product.maximumLaptopSize,
            shoeCompartment: !!product.features.shoes,
            wetBagIncluded: !!product.features.wetBag,
            bottleHolders: product.features.bottleHolders,
            waterResistant: !!product.features.waterResistant,
            antimicrobialLining: !!product.features.antimicrobialLining,
            waistSternumStraps: !!product.features.waistSternumStraps,
            ergonomicAirflowBackPanel: !!product.features.ergonomicAirflowBackPanel,
            cabinBagFriendly: !!product.features.cabinFriendly
          },
          colours: product.colours
        });
      });

      try {
        localStorage.setItem(storageKey, JSON.stringify(state));
      } catch (error) {}
      window.location.assign(this.dataset.comparisonUrl || '/pages/backpack-comparison');
    }

    hideOutcome() {
      if (this.loadingPanel) {
        this.loadingPanel.hidden = true;
        this.loadingPanel.classList.remove('is-active');
      }
      if (this.resultPanel) {
        this.resultPanel.hidden = true;
        this.resultPanel.classList.remove('is-active');
      }
    }

    reset() {
      this.answers = { use: null, carryStyle: null, capacity: null, features: [], preference: null };
      this.currentStepKey = 'use';
      this.selectedColours = {};
      this.selectedAccessories.clear();
      this.results = [];
      this.hideOutcome();
      this.resultContent.innerHTML = '';
      this.message.textContent = '';

      this.allPanels.forEach((panel) => {
        panel.querySelectorAll('[data-quiz-answer]').forEach((button) => {
          button.classList.remove('is-selected');
          button.setAttribute(button.hasAttribute('aria-pressed') ? 'aria-pressed' : 'aria-checked', 'false');
        });
        const count = panel.querySelector('[data-feature-count]');
        if (count) count.textContent = '0';
        const continueButton = panel.querySelector('[data-quiz-continue]');
        if (continueButton) continueButton.disabled = true;
      });

      try {
        localStorage.removeItem(this.dataset.storageKey);
        sessionStorage.removeItem(`${this.dataset.storageKey || 'bfaBackpackFinder'}:analyticsSession`);
      } catch (error) {}

      this.quizStarted = false;
      this.quizSessionId = this.getQuizSessionId();
      this.renderStep(false);
    }

    save() {
      try {
        localStorage.setItem(this.dataset.storageKey, JSON.stringify({
          answers: this.answers,
          currentStepKey: this.currentStepKey,
          selectedColours: this.selectedColours
        }));
      } catch (error) {}
    }

    restore() {
      try {
        const stored = JSON.parse(localStorage.getItem(this.dataset.storageKey));
        if (!stored) return;

        this.answers = { ...this.answers, ...stored.answers };
        this.selectedColours = stored.selectedColours || {};
        const flow = this.getFlow();
        this.currentStepKey = flow.includes(stored.currentStepKey) ? stored.currentStepKey : flow[0];

        this.allPanels.forEach((panel) => {
          const key = panel.dataset.question;
          const mode = panel.dataset.mode;
          const answer = this.answers[key];
          panel.querySelectorAll('[data-quiz-answer]').forEach((button) => {
            const selected = mode === 'multiple'
              ? (answer || []).includes(button.dataset.value)
              : answer === button.dataset.value;
            button.classList.toggle('is-selected', selected);
            button.setAttribute(mode === 'multiple' ? 'aria-pressed' : 'aria-checked', String(selected));
          });

          if (mode === 'multiple') {
            panel.querySelector('[data-feature-count]').textContent = (answer || []).length;
            panel.querySelector('[data-quiz-continue]').disabled = !(answer || []).length;
          }
        });
      } catch (error) {}
    }

    announce(text) {
      this.message.textContent = '';
      requestAnimationFrame(() => {
        this.message.textContent = text;
      });
    }
  }

  if (!customElements.get('backpack-finder')) {
    customElements.define('backpack-finder', BackpackFinder);
  }
})();
